import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import { isOnIndustryStepAndCredentialVerifiedNewUser } from '../experiments/acq111/acq111Utils';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { getFlowSteps } from '../flow-initial/getFlowConfig';
import { getIsGoogleCredentialAuthentication } from '../google-authentication/useGoogleCredentialForAuthentication';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { useIsFramedCentredStep } from '../hooks/useIsFramedCentredStep';
import { useIsWideStep } from '../hooks/useIsWideStep';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { getFlowKey } from '../routing/getFlowKey';
import { Data } from '../signup-data/DATA';
import { useSignupData } from '../signup-data/useSignupData';
import { getIsOAuthExistingUser } from '../store/app/selectors';
import { Step } from '../views/Step';
import { getMessageComponent } from './getMessageComponent';
import { getMessageKey } from './getMessageKey';
import { MessageKey } from './MessageKey';
import { MessagingConfig } from './MessagingConfig';

const useMessageKey = () => {
  const {
    currentStep
  } = useCurrentStep();
  const {
    data: industry
  } = useSignupData(Data.Industry);
  const {
    isMobile
  } = useResponsiveness();
  const isGoogleCredentialAuthentication = useSelector(getIsGoogleCredentialAuthentication);
  const isExistingOAuthUser = useSelector(getIsOAuthExistingUser);
  const {
    isAssignedToVariant: isAssignedToVariantAcq123
  } = useExperiment(ExperimentKeys.ACQ123, TrackWhen.never);

  if (isAssignedToVariantAcq123()) {
    const flowSpecificMessaging = MessagingConfig.Desktop.get(getFlowKey());
    const messageKey = flowSpecificMessaging.get(Step.LandingPage);
    const postAccountCreation = getFlowSteps().postAccountCreation;
    const isPostAccountCreationStep = postAccountCreation.includes(currentStep);
    const stepsWithoutMessagingOnMobile = [Step.CompanyName, Step.CompanySize, Step.CompanyDomain, Step.CodeVerification, Step.Password];
    const isStepWithoutMessagingOnMobile = isMobile && stepsWithoutMessagingOnMobile.includes(currentStep);

    if (!isStepWithoutMessagingOnMobile && !isPostAccountCreationStep) {
      return {
        messageKey
      };
    }
  }

  if (isOnIndustryStepAndCredentialVerifiedNewUser(currentStep, isGoogleCredentialAuthentication, isExistingOAuthUser)) {
    return {
      messageKey: MessageKey.GetStartedWithSalesHub
    };
  }

  return {
    messageKey: getMessageKey({
      currentStep,
      industry,
      isMobile
    })
  };
};

export const useMessage = () => {
  const {
    isWideStep
  } = useIsWideStep();
  const {
    isFramedCentredStep
  } = useIsFramedCentredStep();
  const {
    shouldShowMessaging
  } = useSignupEmbedder();
  const {
    messageKey
  } = useMessageKey();
  const {
    currentStep
  } = useCurrentStep();
  const excludedSteps = [Step.CodeVerification, Step.ResendEmail];
  const {
    isActive: isInstanceOfSignupEmbedder
  } = useSignupEmbedder();
  const MessageComponent = useMemo(() => {
    if (messageKey) {
      return getMessageComponent({
        messageKey
      });
    }

    return null;
  }, // Do not re-render when just the industry is changing and not the step: that's when the user is typing.
  // We can change this once we make it a dropdown.
  [messageKey]);
  const noMessagingDisplayed = isInstanceOfSignupEmbedder && !shouldShowMessaging || isWideStep || isFramedCentredStep && !excludedSteps.includes(currentStep);
  const shouldDisplayMessaging = !noMessagingDisplayed;
  return {
    component: MessageComponent,
    shouldDisplayMessaging: shouldDisplayMessaging && MessageComponent
  };
};